import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import cpApi from "@/lib/clientPortalApi";
import { PageHeader, fmtDate, fmtDateTime, StatusPill } from "./_cpShared";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Paperclip, Upload, ChevronLeft, X, Download } from "lucide-react";

export default function CpRfiDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rfi, setRfi] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]); // [{id, original_filename, size}]
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await cpApi.get(`/client-portal/rfis/${id}`);
        if (!active) return;
        setRfi(r.data.rfi);
        setResponses(r.data.responses);
      } catch (err) {
        if (!active) return;
        toast.error(err?.response?.data?.detail || "Could not load query");
        navigate("/client-portal/queries");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, reloadKey, navigate]);

  const reload = () => setReloadKey((k) => k + 1);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await cpApi.post("/client-portal/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, r.data]);
      toast.success(`Attached ${f.name}`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onSubmit = async () => {
    if (!text.trim() && attachments.length === 0) {
      return toast.error("Please add a response or attachment");
    }
    setSubmitting(true);
    try {
      await cpApi.post(`/client-portal/rfis/${id}/respond`, {
        response_text: text.trim(),
        attachment_ids: attachments.map((a) => a.id),
      });
      setText("");
      setAttachments([]);
      reload();
      toast.success("Response submitted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadUrl = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/client-portal/files/${fileId}/download`;

  if (loading) {
    return <div className="min-h-[300px] flex items-center justify-center"><Loader2 className="animate-spin" size={28} /></div>;
  }
  if (!rfi) return null;

  return (
    <div>
      <PageHeader
        subtitle={
          <button onClick={() => navigate("/client-portal/queries")} className="flex items-center gap-1 hover:text-[#0a2540]" data-testid="cp-rfi-back">
            <ChevronLeft size={12} /> Back to queries
          </button>
        }
        title={rfi.title}
      />
      <div className="p-4 sm:p-8 max-w-3xl space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={rfi.status} />
          <span className={`text-[10px] tracking-[0.18em] uppercase font-semibold px-1.5 py-0.5 rounded-sm ${rfi.priority === "High" ? "bg-red-100 text-red-800" : rfi.priority === "Low" ? "bg-slate-100" : "bg-amber-100 text-amber-800"}`}>{rfi.priority}</span>
          {rfi.due_date && <span className="text-xs text-slate-500">Due {fmtDate(rfi.due_date)}</span>}
          <span className="text-xs text-slate-400">· Asked {fmtDate(rfi.created_at)} by {rfi.created_by_staff_name || "Griffin"}</span>
        </div>

        {rfi.description && (
          <div className="bg-white border border-slate-200 p-4 rounded-sm text-sm whitespace-pre-wrap text-slate-700" data-testid="cp-rfi-description">
            {rfi.description}
          </div>
        )}

        {rfi.attachment_ids?.length > 0 && (
          <div>
            <div className="text-xs tracking-[0.18em] uppercase text-slate-500 mb-2">Attachments from Griffin</div>
            <ul className="space-y-1">
              {rfi.attachment_ids.map((fid) => (
                <li key={fid} className="text-xs">
                  <a href={downloadUrl(fid)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0a2540] hover:underline">
                    <Paperclip size={12} /> Download attachment <Download size={11} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Responses thread */}
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-slate-500 mb-2">Responses ({responses.length})</div>
          {responses.length === 0 ? (
            <div className="text-sm text-slate-400 italic">No responses yet.</div>
          ) : (
            <ul className="space-y-3">
              {responses.map((r) => {
                const isStaff = r.responder_role === "griffin_staff";
                return (
                <li key={r.id} className={`border p-3 rounded-sm ${isStaff ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`} data-testid={`cp-response-${r.id}`}>
                  <div className="text-[11px] text-slate-500 mb-1">
                    <span className="font-semibold text-slate-700">{r.responder_name}</span>
                    <span className="ml-1.5 text-[9px] tracking-wide uppercase px-1.5 py-0.5 rounded-sm bg-white border">
                      {isStaff ? "Griffin" : (r.responder_role === "company_admin" ? "Company Admin" : "Employee")}
                    </span>
                    <span> · {fmtDateTime(r.created_at)}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{r.response_text || <span className="italic text-slate-400">(no text)</span>}</div>
                  {r.attachment_ids?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {r.attachment_ids.map((fid) => (
                        <li key={fid} className="text-xs">
                          <a href={downloadUrl(fid)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0a2540] hover:underline">
                            <Paperclip size={12} /> Download attachment
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Reply form */}
        {rfi.status !== "Closed" ? (
          <div className="bg-white border border-slate-200 p-4 rounded-sm" data-testid="cp-rfi-reply-form">
            <div className="text-xs tracking-[0.18em] uppercase text-slate-500 mb-2">Your response</div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type your reply…"
              className="min-h-[100px]"
              data-testid="cp-rfi-reply-text"
            />
            {attachments.length > 0 && (
              <ul className="mt-2 space-y-1">
                {attachments.map((a) => (
                  <li key={a.id} className="text-xs flex items-center justify-between bg-slate-50 px-2 py-1 rounded-sm">
                    <span className="flex items-center gap-2"><Paperclip size={12} /> {a.original_filename}</span>
                    <button onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))} className="text-slate-400 hover:text-red-600"><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between mt-3">
              <div>
                <input ref={fileRef} type="file" onChange={onPickFile} className="hidden" data-testid="cp-rfi-file-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="cp-rfi-attach-btn">
                  <Upload size={14} className="mr-2" /> Attach file
                </Button>
              </div>
              <Button onClick={onSubmit} disabled={submitting} className="bg-[#0a2540] hover:bg-[#0a2540]/90" data-testid="cp-rfi-submit">
                {submitting ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                Submit response
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-xs text-slate-500 italic">This query is closed.</div>
        )}
      </div>
    </div>
  );
}
